import React, { useCallback, useContext, useEffect, useState } from "react";
import { Affix } from "antd";
import { withRouter, useLocation } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { connect, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { withOktaAuth } from "@okta/okta-react";
import { CoreTopBar } from "@tailormed/common-client/component/AppShell";
import { logout } from "@tailormed/common-client/services/auth";
import { selectIsDevEnvironment } from "@tailormed/common-client/store/selector";
import { ReactComponent as SecureUploadIcon } from "@tailormed/common-client/assets/svg/icon-menu-upload.svg";
import { ReactComponent as OnlineWizardIcon } from "@tailormed/common-client/assets/svg/icon-menu-sparkle.svg";
import { ReactComponent as SignOutIcon } from "@tailormed/common-client/assets/svg/icon-menu-logout.svg";

import ACTIONS from "./../store/action";
import { clearAllCaches } from "../utils/cache";
import { Routes } from "../constant/routes";
import { selectIsPhiAllowed, selectUser, selectIDPConfig } from "../store/selector";
import { HeaderSearchBar } from "./HeaderSearchBar/HeaderSearchBar";
import { Tasks } from "./Tasks/Tasks";
import { Notifications } from "./Notifications";
import { PatientConfirmationAlertContext } from "../context/PatientConfirmationAlertContext";
import { oktaConfig } from "../constant/okta";
import BulkActionInProgressToast from "./BulkActions/BulkActionInProgressToast";
import { resetMixpanelUser } from "../utils/mixpanel/mixPanelUtils";

const MainHeaderComponent = ({ t, history, user, isPhiAllowed, idpConfig, authState, isDevEnvironment }) => {
  const dispatch = useDispatch();
  const [menuVisible, setMenuVisible] = useState(false);
  const { pathname } = useLocation();
  const { showCancelConfirm } = useContext(PatientConfirmationAlertContext);

  const handleLogOut = useCallback(() => {
    clearAllCaches();
    try {
      resetMixpanelUser();
    } catch (error) {
      // do not break logout if analytics fails
    }
    logout(user, idpConfig, oktaConfig.loginURI);
  }, [user, idpConfig]);

  useEffect(() => {
    // This resets the search results page crumb so the back button on the patient page works correctly
    if (pathname?.substr(0, 9) !== "/patient/" && pathname?.substr(0, 15) !== "/search-results") {
      dispatch(ACTIONS.setSearchResultPage(false));
    }
  }, [pathname]);

  const openSecureUploadModal = () => dispatch(ACTIONS.setShowSecureUpload(true));

  const handleUploadClick = useCallback(() => {
    if (pathname.includes(Routes.NEW_PATIENT)) {
      return showCancelConfirm(Routes.UPLOADER);
    }
    if (pathname !== Routes.UPLOADER) {
      setMenuVisible(false);
      openSecureUploadModal();
    }
  }, [pathname]);

  const handleOnlineWizardClick = useCallback(() => {
    if (pathname !== Routes.ONLINE_WIZARD) {
      setMenuVisible(false);
      history.push(Routes.ONLINE_WIZARD);
    }
  }, [pathname]);

  const profileMenuItems = [
    {
      key: "uploader",
      id: "secure_upload",
      icon: <SecureUploadIcon />,
      label: t("uploader_secure_upload"),
      onClick: handleUploadClick
    },
    {
      key: "online_wizard",
      icon: <OnlineWizardIcon />,
      label: t("online_wizard.title"),
      onClick: handleOnlineWizardClick,
      hidden: !(isDevEnvironment && !user?.isExternal)
    },
    {
      key: "logout",
      id: "sign_out",
      icon: <SignOutIcon />,
      label: t("sign_out"),
      onClick: handleLogOut
    }
  ];

  return (
    <Affix>
      <>
        <BulkActionInProgressToast />
        <CoreTopBar
          search={<HeaderSearchBar />}
          actions={[isPhiAllowed && <Tasks key="tasks" />, <Notifications key="notifications" />].filter(Boolean)}
          avatar={{
            initials: `${user ? user.firstName.substring(0, 1) : ""} ${user ? user.lastName.substring(0, 1) : ""}`,
            isAuthenticated: authState?.isAuthenticated,
            open: menuVisible,
            onOpenChange: setMenuVisible,
            menuItems: profileMenuItems
          }}
        />
      </>
    </Affix>
  );
};

const mapStateToProps = createStructuredSelector({
  user: selectUser,
  isPhiAllowed: selectIsPhiAllowed,
  idpConfig: selectIDPConfig,
  isDevEnvironment: selectIsDevEnvironment
});

const MainHeader = compose(withOktaAuth, withRouter, withTranslation(), connect(mapStateToProps))(MainHeaderComponent);
export { MainHeader };
